import { queryOptions } from '@tanstack/react-query';
import { authedGet } from '../../api/http';
import type {
  ProviderTraffic,
  ProviderTrafficEndpoint,
  ProviderTrafficPoint,
} from '../../data/providerTraffic';
import type { DiscoverySource } from '../../data/discovery';
import { invalid, readNonEmptyString, readRecord } from '../readers';

const sourceAt = (value: unknown): DiscoverySource => {
  const raw = readRecord(value, 'traffic.source');
  const kind = readNonEmptyString(raw.kind, 'traffic.source.kind');
  if (kind !== 'anonymous' && kind !== 'brand') return invalid('traffic.source.kind');
  return {
    id: readNonEmptyString(raw.id, 'traffic.source.id'),
    name: readNonEmptyString(raw.name, 'traffic.source.name'),
    kind,
    artworkKey: readNonEmptyString(raw.artworkKey, 'traffic.source.artworkKey'),
  };
};

const endpointAt = (value: unknown, path: string): ProviderTrafficEndpoint => {
  const raw = readRecord(value, path);
  if (typeof raw.requests !== 'number'
    || !Number.isSafeInteger(raw.requests)
    || raw.requests < 0) return invalid(`${path}.requests`);
  return { sku: readNonEmptyString(raw.sku, `${path}.sku`), requests: raw.requests };
};

const pointAt = (value: unknown, index: number): ProviderTrafficPoint => {
  const path = `traffic.points[${index}]`;
  const raw = readRecord(value, path);
  const t = readNonEmptyString(raw.t, `${path}.t`);
  if (Number.isNaN(Date.parse(t)) || !Array.isArray(raw.endpoints)) return invalid(path);
  return {
    t,
    endpoints: raw.endpoints.map((endpoint, endpointIndex) =>
      endpointAt(endpoint, `${path}.endpoints[${endpointIndex}]`)),
  };
};

export const readProviderTraffic = (value: unknown): ProviderTraffic => {
  const raw = readRecord(value, 'traffic');
  if (raw.provider !== 'AnyAPI') return invalid('traffic.provider');
  if (raw.window !== '30d') return invalid('traffic.window');
  if (raw.resolution !== 'day') return invalid('traffic.resolution');
  if (!Array.isArray(raw.points)) return invalid('traffic.points');
  return {
    provider: 'AnyAPI',
    source: sourceAt(raw.source),
    window: '30d',
    resolution: 'day',
    points: raw.points.map(pointAt),
  };
};

const fetchProviderTraffic = async (providerId: string): Promise<ProviderTraffic> =>
  readProviderTraffic(await authedGet<unknown>(
    `/providers/${encodeURIComponent(providerId)}/traffic`,
  ));

export const providerTrafficQueryOptions = (providerId: string, enabled = true) =>
  queryOptions({
    queryKey: ['provider-traffic', providerId] as const,
    queryFn: () => fetchProviderTraffic(providerId),
    enabled,
    staleTime: 2 * 60 * 1_000,
    refetchInterval: 2 * 60 * 1_000,
  });
